import React, { useState, useEffect } from 'react';
import { LogOut, Layers, TrendingUp, Coins, Zap, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

import AdminLogin from './admin/AdminLogin';
import ApplicationsPanel from './admin/ApplicationsPanel';
import FundingQualifierPanel from './admin/FundingQualifierPanel';
import CoinClaimsPanel from './admin/CoinClaimsPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  const [activeTab, setActiveTab] = useState('applications'); // 'applications' | 'funding' | 'coin'

  // GHL connection test
  const [ghlTesting, setGhlTesting] = useState(false);
  const [ghlResult, setGhlResult] = useState(null);

  // Applications
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterService, setFilterService] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Funding Qualifier
  const [fundingItems, setFundingItems] = useState([]);
  const [fundingLoading, setFundingLoading] = useState(false);
  const [tierFilter, setTierFilter] = useState('');

  // Coin claims
  const [coinClaims, setCoinClaims] = useState([]);
  const [coinLoading, setCoinLoading] = useState(false);
  const [coinFilter, setCoinFilter] = useState('pending');

  useEffect(() => {
    const token = sessionStorage.getItem('adminToken');
    if (token) {
      verifyToken(token);
    } else {
      setCheckingAuth(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await axios.post(`${API}/admin/verify`, { token });
      if (response.data.valid) {
        setIsAuthenticated(true);
      } else {
        sessionStorage.removeItem('adminToken');
      }
    } catch (error) {
      sessionStorage.removeItem('adminToken');
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleLogout = async () => {
    const token = sessionStorage.getItem('adminToken');
    if (token) {
      try {
        await axios.post(`${API}/admin/logout`, { token });
      } catch (error) {
        console.error('Admin logout request failed:', error);
      }
    }
    sessionStorage.removeItem('adminToken');
    setIsAuthenticated(false);
  };

  const handleTestGhl = async () => {
    const token = sessionStorage.getItem('adminToken');
    if (!token) return;
    setGhlTesting(true);
    setGhlResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/ghl/test`, { token });
      setGhlResult(data);
    } catch (error) {
      setGhlResult({
        success: false,
        message: error?.response?.data?.detail || error?.message || 'Request failed',
      });
    } finally {
      setGhlTesting(false);
      setTimeout(() => setGhlResult(null), 10000);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchApplications();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && activeTab === 'funding') {
      fetchFundingItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, activeTab, tierFilter]);

  useEffect(() => {
    if (isAuthenticated && activeTab === 'coin') {
      fetchCoinClaims();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, activeTab, coinFilter]);

  const fetchCoinClaims = async () => {
    const token = sessionStorage.getItem('adminToken');
    if (!token) return;
    setCoinLoading(true);
    try {
      const params = new URLSearchParams({ token });
      if (coinFilter) params.append('status', coinFilter);
      const res = await axios.get(`${API}/admin/coin/claims?${params.toString()}`);
      setCoinClaims(res.data?.items || []);
    } catch (err) {
      console.error('Coin claims fetch error:', err);
    } finally {
      setCoinLoading(false);
    }
  };

  const handleCoinApprove = async (claimId) => {
    const token = sessionStorage.getItem('adminToken');
    if (!token) return;
    if (!window.confirm('Approve this shareholder claim? An email with a one-time discount code will be sent to the buyer.')) return;
    try {
      const res = await axios.post(`${API}/admin/coin/approve`, { token, claimId });
      if (res.data?.success) {
        alert(`Approved! Promo code: ${res.data.promoCode}\nEmail sent: ${res.data.emailSent ? 'Yes' : 'No (check SendGrid config)'}`);
        fetchCoinClaims();
      }
    } catch (err) {
      alert('Could not approve: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const handleCoinReject = async (claimId) => {
    const token = sessionStorage.getItem('adminToken');
    if (!token) return;
    const reason = window.prompt('Reason for rejection? (optional, internal note only)') || '';
    try {
      await axios.post(`${API}/admin/coin/reject`, { token, claimId, reason });
      fetchCoinClaims();
    } catch (err) {
      alert('Could not reject: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const fetchFundingItems = async () => {
    const token = sessionStorage.getItem('adminToken');
    if (!token) return;
    setFundingLoading(true);
    try {
      const params = new URLSearchParams({ token });
      if (tierFilter) params.append('tier', tierFilter);
      const res = await axios.get(`${API}/admin/funding-qualifications?${params.toString()}`);
      if (res.data.success) setFundingItems(res.data.items || []);
    } catch (err) {
      console.error('Funding fetch error:', err);
    } finally {
      setFundingLoading(false);
    }
  };

  const downloadFundingPdf = (id) => {
    const token = sessionStorage.getItem('adminToken');
    if (!token) return;
    const url = `${API}/admin/funding-qualifications/${id}/download?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    filterAndSortApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applications, searchTerm, filterService, sortBy]);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/applications`);
      if (response.data.success) {
        setApplications(response.data.applications);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortApplications = () => {
    let filtered = [...applications];

    if (searchTerm) {
      filtered = filtered.filter((app) =>
        app.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.phone.includes(searchTerm)
      );
    }

    if (filterService !== 'all') {
      filtered = filtered.filter((app) => app.serviceInterest === filterService);
    }

    filtered.sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.timestamp) - new Date(a.timestamp);
      if (sortBy === 'oldest') return new Date(a.timestamp) - new Date(b.timestamp);
      if (sortBy === 'name') return a.firstName.localeCompare(b.firstName);
      return 0;
    });

    setFilteredApplications(filtered);
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Name', 'Email', 'Phone', 'Service Interest', 'Credit Score', 'Business Status', 'Funding Goal', 'Message'];
    const csvData = filteredApplications.map((app) => [
      new Date(app.timestamp).toLocaleDateString(),
      `${app.firstName} ${app.lastName}`,
      app.email,
      app.phone,
      app.serviceInterest,
      app.currentCreditScore,
      app.businessStatus,
      app.fundingGoal,
      `"${(app.message || '').replace(/"/g, '""')}"`,
    ]);

    const csv = [headers, ...csvData].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `collins-academy-applications-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (timestamp) =>
    new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  if (checkingAuth) {
    return (
      <div className="dark-page admin-loading">
        <div className="loading-spinner">Verifying access...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={() => setIsAuthenticated(true)} />;
  }

  if (loading) {
    return (
      <div className="dark-page admin-loading">
        <div className="loading-spinner">Loading applications...</div>
      </div>
    );
  }

  return (
    <div className="dark-page admin-dashboard">
      <section className="admin-header">
        <div className="admin-header-content">
          <div className="admin-header-top">
            <h1 className="display-large admin-title">Admin Dashboard</h1>
            <div className="admin-header-actions">
              <button
                onClick={handleTestGhl}
                disabled={ghlTesting}
                className="btn-secondary admin-ghl-test-btn"
                data-testid="admin-ghl-test-btn"
                title="Verify GoHighLevel API token is healthy"
              >
                {ghlTesting ? <Loader2 size={18} className="spin" /> : <Zap size={18} />}
                {ghlTesting ? 'Testing…' : 'Test GHL Connection'}
              </button>
              <button onClick={handleLogout} className="btn-secondary admin-logout-btn" data-testid="admin-logout-btn">
                <LogOut size={18} />
                Logout
              </button>
            </div>
          </div>

          {ghlResult && (
            <div
              className={`admin-ghl-result ${ghlResult.success ? 'admin-ghl-result-ok' : 'admin-ghl-result-err'}`}
              data-testid="admin-ghl-result"
              role="status"
            >
              {ghlResult.success ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
              <div className="admin-ghl-result-body">
                <strong>{ghlResult.success ? 'GHL Connected' : 'GHL Issue'}</strong>
                <span>{ghlResult.message}</span>
                {ghlResult.success && ghlResult.locationName && (
                  <span className="admin-ghl-result-meta">
                    Location: <em>{ghlResult.locationName}</em> · Auth: {ghlResult.tokenType}
                  </span>
                )}
              </div>
            </div>
          )}

          <p className="body-medium admin-subtitle">
            {activeTab === 'applications' ? (
              <>Total Applications: <strong>{applications.length}</strong> | Showing: <strong>{filteredApplications.length}</strong></>
            ) : activeTab === 'funding' ? (
              <>Funding Qualifier Submissions: <strong>{fundingItems.length}</strong></>
            ) : (
              <>$OHBCU Shareholder Claims ({coinFilter || 'all'}): <strong>{coinClaims.length}</strong></>
            )}
          </p>

          <div className="admin-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={activeTab === 'applications'}
              className={`admin-tab ${activeTab === 'applications' ? 'admin-tab-active' : ''}`}
              onClick={() => setActiveTab('applications')}
              data-testid="admin-tab-applications"
            >
              <Layers size={16} /> Applications
            </button>
            <button
              role="tab"
              aria-selected={activeTab === 'funding'}
              className={`admin-tab ${activeTab === 'funding' ? 'admin-tab-active' : ''}`}
              onClick={() => setActiveTab('funding')}
              data-testid="admin-tab-funding"
            >
              <TrendingUp size={16} /> Funding Qualifier
            </button>
            <button
              role="tab"
              aria-selected={activeTab === 'coin'}
              className={`admin-tab ${activeTab === 'coin' ? 'admin-tab-active' : ''}`}
              onClick={() => setActiveTab('coin')}
              data-testid="admin-tab-coin"
            >
              <Coins size={16} /> $OHBCU Claims
            </button>
          </div>
        </div>
      </section>

      {activeTab === 'funding' ? (
        <FundingQualifierPanel
          items={fundingItems}
          loading={fundingLoading}
          tierFilter={tierFilter}
          setTierFilter={setTierFilter}
          onDownload={downloadFundingPdf}
          formatDate={formatDate}
        />
      ) : activeTab === 'coin' ? (
        <CoinClaimsPanel
          items={coinClaims}
          loading={coinLoading}
          statusFilter={coinFilter}
          setStatusFilter={setCoinFilter}
          onApprove={handleCoinApprove}
          onReject={handleCoinReject}
          formatDate={formatDate}
        />
      ) : (
        <ApplicationsPanel
          applications={applications}
          filtered={filteredApplications}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filterService={filterService}
          setFilterService={setFilterService}
          sortBy={sortBy}
          setSortBy={setSortBy}
          onExport={exportToCSV}
          formatDate={formatDate}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
